import type { ChannelRef } from './catalog/channel';
import type { LoadIconKey } from './catalog/icon';
import { RCU_LIMITS } from './catalog/limits';
import type { LoadTypeKey } from './catalog/loadType';
import type { RailTypeKey } from './catalog/railType';
import { SCHEMA_VERSION } from './catalog/storage';
import type { DaliBridgeId, LoadId, RailId, RoomId } from './ids';
import type { Result } from './result';

/** Identity and address of the single RCU this tablet is dedicated to. */
export interface RcuIdentity {
  readonly name: string;
  /**
   * Asset label the installer gives this unit. Also the answer to the PIN
   * recovery prompt, so it has to be written on the hardware.
   */
  readonly deviceId: string;
  /**
   * Serial number the firmware reports and derives its mDNS hostname from
   * (`rcu-<serial>.local`). Used to find the unit on the network when no IP
   * address has been set.
   */
  readonly serialNumber: string;
  /** Optional: set it to skip name resolution, which some tablets cannot do. */
  readonly ipAddress: string;
  readonly location: string;
  /** Onboard relay outputs, 1–26. */
  readonly outputCount: number;
}

/** A DIN rail module fitted to this RCU. */
export interface DinRail {
  readonly id: RailId;
  readonly type: RailTypeKey;
  /** 0–15, unique per type on this RCU. */
  readonly address: number;
  /** Free-form, e.g. `Bedroom ceiling`. */
  readonly label: string;
}

/**
 * A DALI bridge fitted to this RCU. It carries no guest-facing channels: the
 * fixtures behind it are addressed by DALI short address, so the console drives
 * them through the bridge's master and the guest model never sees them.
 */
export interface DaliBridge {
  readonly id: DaliBridgeId;
  /** Rotary address of the bridge module, 0–15. */
  readonly address: number;
  /** DALI bus the bridge owns, 0–15. One bus carries one bridge. */
  readonly master: number;
  readonly label: string;
}

/** A controllable load, wired to exactly one output and optionally one room. */
export interface Load {
  readonly id: LoadId;
  readonly name: string;
  readonly type: LoadTypeKey;
  /** null until the technician assigns the load to a room. */
  readonly roomId: RoomId | null;
  /** null until the load is wired to an output. */
  readonly channel: ChannelRef | null;
  readonly wattage: number | null;
  readonly iconKey: LoadIconKey;
  readonly createdAt: string;
}

export interface Room {
  readonly id: RoomId;
  readonly name: string;
  readonly note: string;
  readonly createdAt: string;
}

/**
 * The whole device-local definition. Everything is `readonly`: changes go
 * through a command in `commands/`, which returns a new configuration or an
 * issue, never a half-applied one.
 */
export interface RcuConfig {
  readonly schemaVersion: number;
  readonly rcu: RcuIdentity;
  readonly rails: readonly DinRail[];
  /** DALI bridges, counted with the rails against the RCU's module budget. */
  readonly daliBridges: readonly DaliBridge[];
  readonly loads: readonly Load[];
  readonly rooms: readonly Room[];
  /** Room this tablet belongs to; preselected on the controls page. */
  readonly defaultRoomId: RoomId | null;
  readonly updatedAt: string;
}

/** New configuration plus whatever the command produced (an id, usually). */
export interface CommandOutcome<T> {
  readonly config: RcuConfig;
  readonly value: T;
}

/** A load that has been wired to an output. */
export type WiredLoad = Load & { readonly channel: ChannelRef };

/**
 * Narrows to wired loads, so anything that reasons about physical outputs never
 * needs a non-null assertion.
 */
export const isWired = (load: Load): load is WiredLoad => load.channel !== null;

/**
 * Whether commands have anywhere to go: a typed IP address, or a serial number
 * to resolve as `rcu-<serial>.local`.
 */
export const hasTarget = (identity: RcuIdentity): boolean =>
  identity.ipAddress.trim() !== '' || identity.serialNumber.trim() !== '';

/** Every command in `commands/` returns this. */
export type CommandResult<T> = Result<CommandOutcome<T>>;

const EMPTY_IDENTITY: RcuIdentity = {
  name: '',
  deviceId: '',
  serialNumber: '',
  ipAddress: '',
  location: '',
  outputCount: RCU_LIMITS.DEFAULT_OUTPUTS,
};

/** `now` is injected so commands and tests stay deterministic. */
export const createEmptyConfig = (now: string): RcuConfig => ({
  schemaVersion: SCHEMA_VERSION,
  rcu: EMPTY_IDENTITY,
  rails: [],
  daliBridges: [],
  loads: [],
  rooms: [],
  defaultRoomId: null,
  updatedAt: now,
});
